/**
 * CRM Links Utility
 *
 * Build URLs and format CRM entity information for display.
 */

import {
  buildCrmEntityUrl as buildUrl,
  type CrmEntityUrlType,
  type CrmProviderType,
} from '@aica/core';

/**
 * The vendor union and the URL shapes used to live here, in a list that had
 * already lost `uspacy` — so a Uspacy record either rendered no link at all
 * (its org has no `bitrix24Domain`) or, in an org connected to BOTH CRMs, a
 * link into the Bitrix24 portal carrying a Uspacy entity id: a real card
 * belonging to someone else.
 *
 * Both shapes and the vendor set now come from `CRM_VENDORS` in @aica/core,
 * shared with the backend.
 */
export type CrmVendor = CrmProviderType;
export type CrmEntityType = CrmEntityUrlType;

/**
 * Build a direct URL to a CRM entity detail page.
 *
 * Returns `null` when the vendor has no known URL shape or no portal domain is
 * available. Callers render plain text on `null` — there is no fallback shape,
 * because every fallback points at a real page in the wrong CRM.
 */
export function buildCrmEntityUrl(
  // `string`, not `CrmVendor`: the vendor now arrives from the API as the
  // record's own `crm_vendor`, which can also name a telephony install. The
  // registry answers "no URL shape" for those, and that is the correct answer.
  vendor: string | null | undefined,
  domain: string | null | undefined,
  entityType: CrmEntityType,
  entityId: string
): string | null {
  if (!vendor) return null;
  return buildUrl({ vendor, domain, entityType, entityId });
}

/**
 * Format CRM entity for display.
 *
 * @param entityType - Type of CRM entity
 * @param entityId - ID of the CRM entity
 * @param entityName - Optional name of the entity
 * @returns Formatted string like "Deal: Company Name" or "Lead #123"
 */
export function formatCrmEntity(
  entityType: CrmEntityType,
  entityId: string,
  entityName?: string | null
): string {
  const labels: Record<CrmEntityType, string> = {
    deal: 'Deal',
    lead: 'Lead',
    contact: 'Contact',
    company: 'Company',
  };

  const label = labels[entityType] || entityType;

  if (entityName) {
    return `${label}: ${entityName}`;
  }

  return `${label} #${entityId}`;
}

/**
 * Get icon name for CRM entity type.
 *
 * @param entityType - Type of CRM entity
 * @returns Icon name for use with lucide-react
 */
export function getCrmEntityIcon(entityType: CrmEntityType): string {
  switch (entityType) {
    case 'deal':
      return 'Briefcase';
    case 'lead':
      return 'Target';
    case 'contact':
      return 'User';
    case 'company':
      return 'Building';
    default:
      return 'Link';
  }
}
